import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Stepper, 
  Step, 
  StepLabel, 
  Paper, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  IconButton,
  Tooltip,
  Fade,
  useTheme,
  Slide,
  Grow,
  CircularProgress,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import { styled } from '@mui/system';
import { supabase } from '../supabaseClient';

// Styled components for the onboarding experience
const OnboardingDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(135deg, #1A1A25 0%, #0A0A0F 100%)' 
      : 'linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
    overflow: 'hidden',
    maxWidth: '800px',
    width: '100%',
    margin: theme.spacing(2),
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: theme.palette.mode === 'dark'
        ? `radial-gradient(circle at 50% 50%, rgba(138, 43, 226, 0.15) 0%, transparent 70%),
           repeating-linear-gradient(45deg, rgba(138, 43, 226, 0.1) 0px, rgba(138, 43, 226, 0.1) 1px, transparent 1px, transparent 10px),
           repeating-linear-gradient(-45deg, rgba(147, 112, 219, 0.1) 0px, rgba(147, 112, 219, 0.1) 1px, transparent 1px, transparent 10px)`
        : `radial-gradient(circle at 50% 50%, rgba(138, 43, 226, 0.05) 0%, transparent 70%),
           repeating-linear-gradient(45deg, rgba(138, 43, 226, 0.03) 0px, rgba(138, 43, 226, 0.03) 1px, transparent 1px, transparent 10px),
           repeating-linear-gradient(-45deg, rgba(147, 112, 219, 0.03) 0px, rgba(147, 112, 219, 0.03) 1px, transparent 1px, transparent 10px)`,
      backgroundSize: '100% 100%, 20px 20px, 20px 20px',
      opacity: 0.5,
      zIndex: 0,
      pointerEvents: 'none',
    }
  },
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  '& .MuiStepIcon-root': {
    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
    '&.Mui-active': {
      color: theme.palette.primary.main,
    },
    '&.Mui-completed': {
      color: theme.palette.success.main,
    }
  },
  '& .MuiStepLabel-label': {
    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
    '&.Mui-active': {
      color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
      fontWeight: 600,
    },
    '&.Mui-completed': {
      color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
    }
  },
  '& .MuiStepConnector-line': {
    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: '10px 24px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 4px 12px rgba(138, 43, 226, 0.3)' 
    : '0 4px 12px rgba(138, 43, 226, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 6px 16px rgba(138, 43, 226, 0.4)' 
      : '0 6px 16px rgba(138, 43, 226, 0.3)',
  }
}));

const OptionCard = styled(Paper)(({ theme, selected }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  border: `2px solid ${selected ? theme.palette.primary.main : 'transparent'}`,
  backgroundColor: theme.palette.mode === 'dark' 
    ? selected ? 'rgba(138, 43, 226, 0.15)' : 'rgba(26, 26, 37, 0.8)'
    : selected ? 'rgba(138, 43, 226, 0.05)' : '#FFFFFF',
  boxShadow: selected 
    ? `0 4px 12px ${theme.palette.primary.main}30` 
    : theme.palette.mode === 'dark'
      ? '0 2px 8px rgba(0, 0, 0, 0.2)'
      : '0 2px 8px rgba(0, 0, 0, 0.05)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: selected 
      ? `0 6px 16px ${theme.palette.primary.main}40` 
      : '0 6px 16px rgba(0, 0, 0, 0.1)',
    backgroundColor: theme.palette.mode === 'dark' 
      ? selected ? 'rgba(138, 43, 226, 0.2)' : 'rgba(26, 26, 37, 0.9)'
      : selected ? 'rgba(138, 43, 226, 0.08)' : '#FFFFFF',
  }
}));

const ProgressIndicator = styled(Box)(({ theme, progress }) => ({
  height: 4,
  width: '100%',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
  borderRadius: 2,
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: `${progress}%`,
    backgroundColor: theme.palette.primary.main,
    borderRadius: 2,
    transition: 'width 0.5s ease-in-out'
  }
}));

const Tooltip1 = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: theme.palette.mode === 'dark' ? '#272733' : '#FFFFFF',
    color: theme.palette.mode === 'dark' ? '#E0E0E5' : '#000000',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    borderRadius: 8,
    padding: theme.spacing(2),
    maxWidth: 300,
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    fontSize: '0.875rem',
  },
}));

// Define the onboarding steps
const steps = [
  'Hoş Geldiniz',
  'Rolünüz',
  'Hedefleriniz',
  'Deneyim Seviyeniz',
  'Özelleştirme'
];

// Role options with icons and descriptions
const roleOptions = [
  { 
    id: 'architect', 
    title: 'Mimar', 
    icon: '🏛️', 
    description: 'Mimari tasarım ve görselleştirme için araçlar' 
  },
  { 
    id: 'interior_designer', 
    title: 'İç Mimar', 
    icon: '🪑', 
    description: 'İç mekan tasarımı ve dekorasyon için araçlar' 
  },
  { 
    id: 'real_estate', 
    title: 'Emlak Profesyoneli', 
    icon: '🏠', 
    description: 'Emlak pazarlaması ve sunumları için araçlar' 
  },
  { 
    id: 'student', 
    title: 'Öğrenci', 
    icon: '🎓', 
    description: 'Eğitim ve öğrenme amaçlı araçlar' 
  },
  { 
    id: 'other', 
    title: 'Diğer', 
    icon: '✨', 
    description: 'Diğer kullanım amaçları için araçlar' 
  }
];

// Goal options
const goalOptions = [
  { 
    id: 'client_presentations', 
    title: 'Müşteri Sunumları', 
    description: 'Müşterilere sunmak için yüksek kaliteli görseller oluşturun' 
  },
  { 
    id: 'concept_exploration', 
    title: 'Konsept Keşfi', 
    description: 'Farklı tasarım fikirlerini hızlıca keşfedin ve deneyin' 
  },
  { 
    id: 'portfolio_building', 
    title: 'Portföy Oluşturma', 
    description: 'Profesyonel portföyünüz için etkileyici görseller yaratın' 
  },
  { 
    id: 'marketing_materials', 
    title: 'Pazarlama Materyalleri', 
    description: 'Pazarlama ve tanıtım için görseller oluşturun' 
  }
];

// Experience level options
const experienceLevels = [
  { id: 'beginner', title: 'Yeni Başlayan', description: 'Render araçlarını ilk kez kullanıyorum' },
  { id: 'intermediate', title: 'Orta Seviye', description: 'Temel render bilgisine sahibim' },
  { id: 'advanced', title: 'İleri Seviye', description: 'Render araçlarında deneyimliyim' }
];

// Preference options
const preferenceOptions = [
  { id: 'tutorials', title: 'Eğitim İpuçları', description: 'Platformu kullanırken yardımcı ipuçları göster' },
  { id: 'notifications', title: 'Bildirimler', description: 'Yeni özellikler ve güncellemeler hakkında bildirimler al' },
  { id: 'sample_projects', title: 'Örnek Projeler', description: 'Örnek projeleri ve şablonları göster' }
];

const Onboarding = ({ open, onClose, onComplete }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState(null);
  
  // User preferences state
  const [userProfile, setUserProfile] = useState({
    role: '',
    goals: [],
    experienceLevel: '',
    preferences: {
      tutorials: true,
      notifications: true,
      sample_projects: true
    },
    name: '',
    company: ''
  });
  
  // Handle step navigation
  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleComplete();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };
  
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };
  
  // Handle role selection
  const handleRoleSelect = (roleId) => {
    setUserProfile(prev => ({
      ...prev,
      role: roleId
    }));
  };
  
  // Handle goal selection (multiple)
  const handleGoalToggle = (goalId) => {
    setUserProfile(prev => {
      const currentGoals = [...prev.goals];
      const goalIndex = currentGoals.indexOf(goalId);
      
      if (goalIndex === -1) {
        currentGoals.push(goalId);
      } else {
        currentGoals.splice(goalIndex, 1);
      }
      
      return {
        ...prev,
        goals: currentGoals
      };
    });
  };
  
  // Handle experience level selection
  const handleExperienceSelect = (levelId) => {
    setUserProfile(prev => ({
      ...prev,
      experienceLevel: levelId
    }));
  };
  
  // Handle preference toggles
  const handlePreferenceToggle = (preferenceId) => {
    setUserProfile(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [preferenceId]: !prev.preferences[preferenceId]
      }
    }));
  };
  
  // Handle text input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Complete onboarding and save preferences
  const handleComplete = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        console.log('Saving onboarding preferences for user:', user.id);
        console.log('Preferences to save:', {
          role: userProfile.role,
          goals: userProfile.goals,
          experienceLevel: userProfile.experienceLevel,
          preferences: userProfile.preferences,
          name: userProfile.name,
          company: userProfile.company
        });
        
        // Save onboarding preferences to user profile
        const { error } = await supabase
          .from('profiles')
          .update({
            onboarding_completed: true,
            role: userProfile.role,
            goals: userProfile.goals,
            experience_level: userProfile.experienceLevel,
            preferences: userProfile.preferences,
            display_name: userProfile.name || null,
            company: userProfile.company || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
          
        if (error) {
          console.error('Supabase update error:', error);
          throw error;
        }
        
        console.log('Onboarding preferences saved successfully');
      } else {
        console.error('No user found when trying to save onboarding preferences');
        throw new Error('User not authenticated');
      }
      
      setCompleted(true);
      
      // Show completion animation for 2 seconds before closing
      setTimeout(() => {
        if (onComplete) {
          onComplete(userProfile);
        }
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error saving onboarding preferences:', error);
      setError('Tercihleriniz kaydedilirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };
  
  // Skip onboarding
  const handleSkip = async () => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Mark onboarding as completed without preferences
        await supabase
          .from('profiles')
          .update({
            onboarding_completed: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
      }
      
      onClose();
    } catch (error) {
      console.error('Error skipping onboarding:', error);
      onClose();
    }
  };
  
  // Calculate progress percentage
  const calculateProgress = () => {
    const totalSteps = steps.length;
    return ((activeStep + 1) / totalSteps) * 100;
  };
  
  // Check if next button should be disabled
  const isNextDisabled = () => {
    switch (activeStep) {
      case 0: // Welcome step - always enabled
        return false;
      case 1: // Role selection
        return !userProfile.role;
      case 2: // Goals selection
        return userProfile.goals.length === 0;
      case 3: // Experience level
        return !userProfile.experienceLevel;
      case 4: // Preferences - always enabled
        return false;
      default:
        return false;
    }
  };
  
  // Render step content based on active step
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Fade in={activeStep === 0} timeout={500}>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: theme.palette.primary.main }}>
                Sanalyapı'ya Hoş Geldiniz!
              </Typography>
              
              <Box 
                component="img" 
                src="/logo.png" 
                alt="Sanalyapı Logo"
                sx={{ 
                  width: 120, 
                  height: 120, 
                  mb: 3,
                  filter: theme.palette.mode === 'dark' ? 'drop-shadow(0 0 10px rgba(138, 43, 226, 0.5))' : 'none'
                }}
              />
              
              <Typography variant="body1" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
                Sanalyapı'nın yapay zeka destekli mimari görselleştirme platformuna hoş geldiniz. 
                Size en iyi deneyimi sunabilmemiz için birkaç dakikanızı ayırarak tercihlerinizi belirtmenizi rica ediyoruz.
              </Typography>
              
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Bu kurulum sadece birkaç dakika sürecek ve deneyiminizi kişiselleştirmemize yardımcı olacak.
              </Typography>
            </Box>
          </Fade>
        );
        
      case 1:
        return (
          <Fade in={activeStep === 1} timeout={500}>
            <Box sx={{ py: 2 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Rolünüzü Seçin
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                Sizin için en uygun araçları ve önerileri sunabilmemiz için rolünüzü seçin.
              </Typography>
              
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2, 
                mb: 3 
              }}>
                {roleOptions.map((role) => (
                  <OptionCard 
                    key={role.id}
                    selected={userProfile.role === role.id}
                    onClick={() => handleRoleSelect(role.id)}
                    elevation={userProfile.role === role.id ? 4 : 1}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6" sx={{ mr: 1 }}>
                        {role.icon}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {role.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {role.description}
                    </Typography>
                  </OptionCard>
                ))}
              </Box>
            </Box>
          </Fade>
        );
        
      case 2:
        return (
          <Fade in={activeStep === 2} timeout={500}>
            <Box sx={{ py: 2 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Hedefleriniz
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                Sanalyapı'yı hangi amaçlarla kullanmayı planlıyorsunuz? (Birden fazla seçebilirsiniz)
              </Typography>
              
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2, 
                mb: 3 
              }}>
                {goalOptions.map((goal) => (
                  <OptionCard 
                    key={goal.id}
                    selected={userProfile.goals.includes(goal.id)}
                    onClick={() => handleGoalToggle(goal.id)}
                    elevation={userProfile.goals.includes(goal.id) ? 4 : 1}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {goal.title}
                      </Typography>
                      <Checkbox 
                        checked={userProfile.goals.includes(goal.id)}
                        onChange={() => handleGoalToggle(goal.id)}
                        sx={{ 
                          color: theme.palette.primary.main,
                          '&.Mui-checked': {
                            color: theme.palette.primary.main,
                          }
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {goal.description}
                    </Typography>
                  </OptionCard>
                ))}
              </Box>
            </Box>
          </Fade>
        );
        
      case 3:
        return (
          <Fade in={activeStep === 3} timeout={500}>
            <Box sx={{ py: 2 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Deneyim Seviyeniz
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                Mimari görselleştirme araçlarıyla ilgili deneyim seviyenizi seçin.
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                {experienceLevels.map((level) => (
                  <OptionCard 
                    key={level.id}
                    selected={userProfile.experienceLevel === level.id}
                    onClick={() => handleExperienceSelect(level.id)}
                    elevation={userProfile.experienceLevel === level.id ? 4 : 1}
                  >
                    <FormControlLabel
                      control={
                        <Radio 
                          checked={userProfile.experienceLevel === level.id}
                          onChange={() => handleExperienceSelect(level.id)}
                          sx={{ 
                            color: theme.palette.primary.main,
                            '&.Mui-checked': {
                              color: theme.palette.primary.main,
                            }
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {level.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {level.description}
                          </Typography>
                        </Box>
                      }
                      sx={{ margin: 0, width: '100%' }}
                    />
                  </OptionCard>
                ))}
              </Box>
            </Box>
          </Fade>
        );
        
      case 4:
        return (
          <Fade in={activeStep === 4} timeout={500}>
            <Box sx={{ py: 2 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Kişisel Bilgiler ve Tercihler
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <TextField
                  fullWidth
                  label="Adınız (İsteğe bağlı)"
                  name="name"
                  value={userProfile.name}
                  onChange={handleInputChange}
                  variant="outlined"
                  margin="normal"
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  label="Şirket/Kurum (İsteğe bağlı)"
                  name="company"
                  value={userProfile.company}
                  onChange={handleInputChange}
                  variant="outlined"
                  margin="normal"
                />
              </Box>
              
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Tercihleriniz
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                {preferenceOptions.map((preference) => (
                  <FormControlLabel
                    key={preference.id}
                    control={
                      <Checkbox 
                        checked={userProfile.preferences[preference.id]}
                        onChange={() => handlePreferenceToggle(preference.id)}
                        sx={{ 
                          color: theme.palette.primary.main,
                          '&.Mui-checked': {
                            color: theme.palette.primary.main,
                          }
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle2">
                          {preference.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {preference.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ display: 'flex', mb: 2 }}
                  />
                ))}
              </Box>
            </Box>
          </Fade>
        );
        
      default:
        return null;
    }
  };
  
  // Render completion screen
  const renderCompletionScreen = () => (
    <Grow in={completed} timeout={800}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        py: 6
      }}>
        <CheckCircleIcon 
          sx={{ 
            fontSize: 80, 
            color: 'success.main',
            mb: 3,
            animation: 'pulse 1.5s infinite ease-in-out',
            '@keyframes pulse': {
              '0%': { transform: 'scale(0.95)', opacity: 0.8 },
              '50%': { transform: 'scale(1.05)', opacity: 1 },
              '100%': { transform: 'scale(0.95)', opacity: 0.8 },
            }
          }} 
        />
        
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 700, textAlign: 'center' }}>
          Kurulum Tamamlandı!
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', maxWidth: 500, mx: 'auto' }}>
          Tercihleriniz kaydedildi. Sanalyapı deneyiminiz artık kişiselleştirildi.
        </Typography>
      </Box>
    </Grow>
  );
  
  return (
    <OnboardingDialog
      open={open}
      onClose={loading || completed ? undefined : onClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading || completed}
    >
      {!completed ? (
        <>
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            pb: 2
          }}>
            <Typography variant="h6">
              Sanalyapı Kurulum
            </Typography>
            
            {!loading && (
              <IconButton 
                edge="end" 
                color="inherit" 
                onClick={onClose}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>
          
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ mb: 4, mt: 2 }}>
              <StyledStepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </StyledStepper>
            </Box>
            
            <ProgressIndicator progress={calculateProgress()} sx={{ mb: 4 }} />
            
            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 3 }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}
            
            {renderStepContent()}
          </DialogContent>
          
          <DialogActions sx={{ 
            justifyContent: 'space-between', 
            px: 3, 
            py: 2,
            borderTop: '1px solid',
            borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          }}>
            <Box>
              {activeStep === 0 && (
                <Button 
                  onClick={handleSkip}
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                  disabled={loading}
                >
                  Şimdilik Geç
                </Button>
              )}
              
              {activeStep > 0 && (
                <StyledButton
                  onClick={handleBack}
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  disabled={loading}
                >
                  Geri
                </StyledButton>
              )}
            </Box>
            
            <StyledButton
              onClick={handleNext}
              variant="contained"
              endIcon={activeStep === steps.length - 1 ? <CheckCircleIcon /> : <ArrowForwardIcon />}
              disabled={isNextDisabled() || loading}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: '#FFFFFF',
                minWidth: 120
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                activeStep === steps.length - 1 ? 'Tamamla' : 'Devam'
              )}
            </StyledButton>
          </DialogActions>
        </>
      ) : (
        renderCompletionScreen()
      )}
    </OnboardingDialog>
  );
};

// Interactive tooltip component for feature highlights
export const FeatureTooltip = ({ 
  children, 
  title, 
  description, 
  placement = 'bottom', 
  showTooltip = true,
  onClose,
  highlight = false
}) => {
  const theme = useTheme();
  
  if (!showTooltip) return children;
  
  return (
    <Tooltip1
      title={
        <Box sx={{ p: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="body2">
            {description}
          </Typography>
          {onClose && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                size="small" 
                onClick={onClose}
                sx={{ 
                  textTransform: 'none',
                  color: theme.palette.primary.main
                }}
              >
                Anladım
              </Button>
            </Box>
          )}
        </Box>
      }
      placement={placement}
      arrow
      open={showTooltip}
    >
      <Box sx={{ 
        position: 'relative',
        '&::after': highlight ? {
          content: '""',
          position: 'absolute',
          inset: -4,
          borderRadius: '8px',
          border: `2px solid ${theme.palette.primary.main}`,
          animation: 'pulse 1.5s infinite',
          pointerEvents: 'none',
          zIndex: 1,
          '@keyframes pulse': {
            '0%': { boxShadow: `0 0 0 0 ${theme.palette.primary.main}40` },
            '70%': { boxShadow: `0 0 0 6px ${theme.palette.primary.main}00` },
            '100%': { boxShadow: `0 0 0 0 ${theme.palette.primary.main}00` }
          }
        } : {}
      }}>
        {children}
      </Box>
    </Tooltip1>
  );
};

// Tutorial tip component for contextual help
export const TutorialTip = ({ 
  id, 
  title, 
  description, 
  placement = 'bottom',
  children,
  showDismiss = true,
  onDismiss
}) => {
  const theme = useTheme();
  const [show, setShow] = useState(true);
  
  const handleDismiss = useCallback(() => {
    setShow(false);
    if (onDismiss) {
      onDismiss(id);
    }
  }, [id, onDismiss]);
  
  if (!show) return children;
  
  return (
    <Box sx={{ position: 'relative' }}>
      {children}
      
      <Box sx={{
        position: 'absolute',
        top: placement === 'bottom' ? '100%' : placement === 'top' ? 'auto' : '50%',
        bottom: placement === 'top' ? '100%' : 'auto',
        left: placement === 'right' ? '100%' : placement === 'left' ? 'auto' : placement === 'bottom' ? '50%' : 'auto',
        right: placement === 'left' ? '100%' : placement === 'bottom' ? 'auto' : '0',
        transform: placement === 'bottom' 
          ? 'translateX(-50%)' 
          : placement === 'top'
            ? 'translateX(0)'
            : placement === 'left' || placement === 'right'
              ? 'translateY(-50%)'
              : 'translateY(-50%)',
        zIndex: 9999, // Increased z-index to ensure it's on top
        mt: placement === 'bottom' ? 2 : 0,
        mb: placement === 'top' ? 2 : 0,
        ml: placement === 'right' ? 2 : 0,
        mr: placement === 'left' ? 2 : 0,
        maxWidth: { xs: 280, sm: 300 },
        width: { xs: 'calc(100vw - 40px)', sm: 'max-content' }
      }}>
        <Paper sx={{
          p: 2,
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
          border: `2px solid ${theme.palette.primary.main}`,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(26, 26, 37, 0.98)' : 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(12px)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            width: 12,
            height: 12,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(26, 26, 37, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            transform: 'rotate(45deg)',
            top: placement === 'bottom' ? -6 : placement === 'top' ? 'auto' : '50%',
            bottom: placement === 'top' ? -6 : 'auto',
            left: placement === 'right' ? -6 : placement === 'left' ? 'auto' : placement === 'bottom' ? '50%' : 'auto',
            right: placement === 'left' ? -6 : placement === 'bottom' ? 'auto' : placement === 'top' ? '10%' : 'auto',
            marginTop: placement === 'left' || placement === 'right' ? -6 : 0,
            marginLeft: placement === 'bottom' ? -6 : placement === 'top' ? 0 : 0,
            border: `1px solid ${theme.palette.primary.main}30`,
            borderRight: placement === 'left' ? 'none' : undefined,
            borderBottom: placement === 'top' ? 'none' : undefined,
            borderLeft: placement === 'right' ? 'none' : undefined,
            borderTop: placement === 'bottom' ? 'none' : undefined,
            zIndex: -1
          }
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            mb: 1,
            animation: 'fadeIn 0.5s ease-in-out',
            '@keyframes fadeIn': {
              '0%': { opacity: 0, transform: 'translateY(-10px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' }
            }
          }}>
            <EmojiObjectsIcon sx={{ 
              color: theme.palette.primary.main, 
              mr: 1,
              animation: 'pulse 2s infinite ease-in-out',
              '@keyframes pulse': {
                '0%': { opacity: 0.7 },
                '50%': { opacity: 1 },
                '100%': { opacity: 0.7 }
              }
            }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
              {title}
            </Typography>
            {showDismiss && (
              <IconButton 
                size="small" 
                onClick={handleDismiss} 
                sx={{ 
                  ml: 1, 
                  mt: -1, 
                  mr: -1,
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  },
                  zIndex: 10
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          
          <Typography variant="body2">
            {description}
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default React.memo(Onboarding);
